import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { Nullable } from 'libs/end-to-end/src/ui-types';
import { OnSelectMultiple } from 'libs/end-to-end/src/ui-types';
import { OnSelectSingle } from 'libs/end-to-end/src/ui-types';
import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { DropdownItem } from 'apps/sigma2-ui/src/components/custom-viz/CustomVizHelpers';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useSessionStorage } from 'apps/sigma2-ui/src/lib/hooks/useSessionStorage';

export const DimensionSelect: React.FC<
  UseDynamicDimensionsPayload & {
    widthClass?: string;
    backgroundColor?: string;
    isProcessing: boolean;
  }
> = (payload) => {
  const { dimensions, type, isProcessing, widthClass } = payload;

  const isDisabled = useMemo(() => {
    if (!dimensions) {
      return true;
    }

    return isProcessing;
  }, [isProcessing, dimensions]);

  const dimensionDropdownItems: DropdownItem[] = useMemo(() => {
    if (!dimensions) {
      return [];
    }
    return dimensions.map((dimension) => {
      return {
        optionValue: dimension.columnHeaderValue,
        optionLabel: dimension.displayName,
      };
    });
  }, [dimensions]);

  return (
    <>
      {type === 'single' && (
        <SelectBoxWithSearch
          widthClass={widthClass}
          label={'Dimension'}
          setSelected={(value) => {
            if (typeof value === 'function' || !value) {
              return;
            }
            payload.onSelectSingle(value);
          }}
          selected={payload.selectedDimension ?? ''}
          list={dimensionDropdownItems}
          type={'single'}
          disabled={isDisabled}
        />
      )}
      {type === 'multi' && (
        <SelectBoxWithSearch
          widthClass={widthClass}
          label={'Dimensions'}
          setSelected={(value) => {
            if (typeof value === 'function') {
              return;
            }
            payload.onSelectMultiple(value);
          }}
          selected={payload.selectedDimensions}
          list={dimensionDropdownItems}
          type={'multi'}
          disabled={isDisabled}
        />
      )}
    </>
  );
};

export type UseDynamicDimensionsParams =
  | UseDynamicDimensionsSingleParams
  | UseDynamicDimensionsMultiParams;

export interface UseDynamicDimensionsSingleParams {
  type: 'single';
  automaticallySelectFirstPreferred: boolean;
  persistKey?: string;
}

export interface UseDynamicDimensionsMultiParams {
  type: 'multi';
  automaticallySelectAll?: boolean;
  persistKey?: string;
}

export type UseDynamicDimensionsPayload =
  | UseDynamicDimensionsSinglePayload
  | UseDynamicDimensionsMultiPayload;

export interface UseDynamicDimensionsSinglePayload {
  type: 'single';
  dimensions: DimensionDefinition[] | null;
  selectedDimension: string | null;
  onSelectSingle: OnSelectSingle;
  resetDimensionsToInitialState: () => void;
}

export interface UseDynamicDimensionsMultiPayload {
  type: 'multi';
  dimensions: DimensionDefinition[] | null;
  selectedDimensions: string[];
  onSelectMultiple: OnSelectMultiple;
  resetDimensionsToInitialState: () => void;
}

export type UseDynamicDimensionReturnType<T = UseDynamicDimensionsParams> =
  T extends UseDynamicDimensionsSingleParams
    ? UseDynamicDimensionsSinglePayload
    : UseDynamicDimensionsMultiPayload;

const DynamicDimensionsLocalStorageSchema = z.object({
  selectedDimensions: z.array(z.string()).nullable(),
});

type DynamicDimensionsLocalStorageSchema = z.infer<
  typeof DynamicDimensionsLocalStorageSchema
>;

// eslint-disable-next-line complexity
const getSelectedDimensions = <T extends UseDynamicDimensionsParams>({
  dimensions,
  searchParams,
  params,
  value,
  preferredDimension,
}: {
  dimensions: DimensionDefinition[];
  searchParams: URLSearchParams;
  params: T;
  value: DynamicDimensionsLocalStorageSchema;
  preferredDimension: Nullable<DimensionDefinition>;
}): Nullable<string[]> => {
  if (searchParams.has('dimension')) {
    const dimensionFromSearchParams = searchParams.get('dimension');
    if (dimensionFromSearchParams) {
      const dimensionExists = dimensions.some(
        (x) => x.columnHeaderValue === dimensionFromSearchParams,
      );
      if (dimensionExists) {
        return [dimensionFromSearchParams];
      }
    }
  }

  if (params.type === 'single') {
    if (value.selectedDimensions && value.selectedDimensions.length > 0) {
      if (
        value.selectedDimensions.every((x) =>
          dimensions.map((x) => x.columnHeaderValue).includes(x),
        )
      ) {
        return value.selectedDimensions;
      }
    }
    if (params.automaticallySelectFirstPreferred && dimensions.length > 0) {
      return preferredDimension ? [preferredDimension.columnHeaderValue] : [];
    }
  } else if (params.type === 'multi') {
    if (value.selectedDimensions) {
      return value.selectedDimensions;
    }
    if (params.automaticallySelectAll) {
      return dimensions?.map((x) => x.columnHeaderValue) ?? [];
    }
  } else {
    throw guardExhaustive(params);
  }

  // For TS, this should never happen
  return null;
};

export const useDynamicDimensions = <T extends UseDynamicDimensionsParams>(
  params: T,
): T extends UseDynamicDimensionsSingleParams
  ? UseDynamicDimensionsSinglePayload
  : UseDynamicDimensionsMultiPayload => {
  const { myObservation } = useObservation();

  const dimensions = useMemo(() => {
    return myObservation?.dimensions;
  }, [myObservation]);

  const { type } = useMemo(() => {
    return params;
  }, [params]);

  const [selectedDimensions, internalSetSelectedDimensions] = useState<
    string[]
  >([]);

  const [value, setValue] = useSessionStorage({
    key: params.persistKey ?? 'useDynamicDimensions',
    schema: DynamicDimensionsLocalStorageSchema,
    defaultValue: { selectedDimensions: null },
  });

  const setSelectedDimensions = useCallback(
    (value: string[]) => {
      internalSetSelectedDimensions(() => {
        return value;
      });
      setValue(() => {
        return {
          selectedDimensions: [...value],
        };
      });
    },
    [setValue],
  );

  const [didAutoLoad, setDidAutoLoad] = useState(false);

  const onSelectSingle: OnSelectSingle = useCallback(
    (value) => {
      if (typeof value === 'function') {
        return;
      }
      internalSetSelectedDimensions(() => {
        return value ? [value] : [];
      });
      setValue(() => {
        return {
          selectedDimensions: value ? [value] : [],
        };
      });
    },
    [setValue],
  );

  const onSelectMultiple: OnSelectMultiple = useCallback(
    (values) => {
      if (typeof values === 'function') {
        return;
      }
      setSelectedDimensions(values);
    },
    [setSelectedDimensions],
  );

  const [searchParams] = useSearchParams();

  const preferredDimension = useMemo(() => {
    return dimensions?.[0] ?? null;
  }, [dimensions]);

  const setDimensionsToInitialState = useCallback(() => {
    if (!dimensions) {
      return;
    }

    const selectedDimensions = getSelectedDimensions({
      params,
      searchParams,
      dimensions,
      value,
      preferredDimension,
    });

    setSelectedDimensions(selectedDimensions ?? []);
  }, [
    setSelectedDimensions,
    params,
    value,
    preferredDimension,
    dimensions,
    searchParams,
  ]);

  useEffect(() => {
    if (didAutoLoad) {
      return;
    }

    if (!myObservation || !dimensions) {
      return;
    }

    setDimensionsToInitialState();
    setDidAutoLoad(true);
  }, [setDimensionsToInitialState, didAutoLoad, myObservation, dimensions]);

  return useMemo(() => {
    if (type === 'single') {
      return {
        type,
        dimensions,
        onSelectSingle,
        selectedDimension: selectedDimensions[0] ?? null,
        resetDimensionsToInitialState: setDimensionsToInitialState,
      } as UseDynamicDimensionReturnType<T>;
    } else if (type === 'multi') {
      return {
        type,
        dimensions,
        onSelectMultiple: onSelectMultiple,
        selectedDimensions,
        resetDimensionsToInitialState: setDimensionsToInitialState,
      } as UseDynamicDimensionReturnType<T>;
    } else {
      throw guardExhaustive(type);
    }
  }, [
    dimensions,
    onSelectMultiple,
    onSelectSingle,
    selectedDimensions,
    type,
    setDimensionsToInitialState,
  ]);
};
